import { Spin } from 'antd';
import React from 'react';
import styled, { css } from 'styled-components';

import useCroppedImage from '../../../hooks/useCroppedImage';
import { useGameMediaFile } from '../../../hooks/useGameMediaFile';
import useSvgImage from '../../../hooks/useSvgImage';
import { CenteredContainer } from '../../../styles/CenteredContainer';
import { CropConfig } from '../../../utils/imageCropUtils';

const CroppedGameImageRenderer = ({
  fileId,
  fileAltText,
  cropConfig,
  fileMimetype,
  isDiscoverTemplate,
  objectFit,
  onImageClicked,
  style,
  brightness,
  isAuthenticated,
  isOwner,
}: {
  fileId: string;
  fileMimetype: string;
  fileAltText?: string | null;
  cropConfig?: CropConfig | null;
  isDiscoverTemplate: boolean;
  objectFit?: string;
  brightness?: number | null;
  onImageClicked?: (url: string) => void;
  style?: React.CSSProperties;
  isAuthenticated: boolean;
  isOwner: boolean;
}) => {
  const { imageUrl, isLoading } = useCroppedImage({
    fileId,
    cropConfig,
    fileMimetype,
    isDiscoverTemplate,
    isAuthenticated,
    isOwner,
  });

  if (isLoading && !imageUrl) {
    return (
      <CenteredContainer style={{ height: '100%' }}>
        <Spin />
      </CenteredContainer>
    );
  }

  return (
    <>
      <ImgStyled
        $objectFit={objectFit}
        $brightness={brightness}
        src={imageUrl}
        alt={fileAltText || ''}
        onClick={() => onImageClicked && imageUrl && onImageClicked(imageUrl)}
        $clickable={!!onImageClicked}
        style={style}
      />
    </>
  );
};

const SvgFileRenderer = ({
  fileId,
  isAuthenticated,
  fileAltText,
  isOwner,
}: {
  fileId: string;
  isAuthenticated: boolean;
  fileAltText?: string | null;
  isOwner: boolean;
}) => {
  const { svgText, isLoading } = useSvgImage({
    fileId,
    fileMimetype: 'image/svg+xml',
    isDiscoverTemplate: false,
    isAuthenticated: isAuthenticated,
    isOwner: isOwner,
    colors: [],
  });

  if (isLoading && !svgText) {
    return (
      <CenteredContainer style={{ height: '100%', width: '100%' }}>
        <Spin />
      </CenteredContainer>
    );
  }

  return (
    <SvgImg
      aria-label={fileAltText || ''}
      dangerouslySetInnerHTML={{ __html: svgText }}
    />
  );
};

const GifFileRenderer = ({
  fileId,
  isAuthenticated,
  objectFit,
  brightness,
  fileAltText,
  style,
}: {
  fileId: string;
  isAuthenticated: boolean;
  objectFit?: string;
  brightness?: number | null;
  fileAltText?: string | null;
  style?: React.CSSProperties;
}) => {
  const { data, isLoading } = useGameMediaFile(fileId, isAuthenticated);

  if (isLoading && !data) {
    return (
      <CenteredContainer style={{ height: '100%' }}>
        <Spin />
      </CenteredContainer>
    );
  }

  return (
    <ImgStyled
      $objectFit={objectFit}
      $brightness={brightness}
      src={data}
      alt={fileAltText || ''}
      $clickable={false}
      style={style}
    />
  );
};

const GameImageRenderer = ({
  fileId,
  fileAltText,
  cropConfig,
  fileMimetype,
  isDiscoverTemplate,
  objectFit,
  onImageClicked,
  style,
  brightness,
  isAuthenticated,
  isOwner,
}: {
  fileId: string;
  fileMimetype: string;
  fileAltText?: string | null;
  cropConfig?: CropConfig | null;
  isDiscoverTemplate: boolean;
  objectFit?: string;
  brightness?: number | null;
  onImageClicked?: (url: string) => void;
  style?: React.CSSProperties;
  isAuthenticated: boolean;
  isOwner: boolean;
}) => {
  // @depricated by new mediaUrl
  if (fileMimetype === 'image/gif') {
    return (
      <GifFileRenderer
        fileAltText={fileAltText}
        fileId={fileId}
        brightness={brightness}
        isAuthenticated={isAuthenticated}
        objectFit={objectFit}
        style={style}
      />
    );
  }

  if (fileId.includes('.svg')) {
    return (
      <SvgFileRenderer
        fileAltText={fileAltText}
        fileId={fileId}
        isAuthenticated={isAuthenticated}
        isOwner={isOwner}
      />
    );
  }

  return (
    <CroppedGameImageRenderer
      fileAltText={fileAltText}
      fileId={fileId}
      fileMimetype={fileMimetype}
      brightness={brightness}
      cropConfig={cropConfig}
      isDiscoverTemplate={isDiscoverTemplate}
      isAuthenticated={isAuthenticated}
      objectFit={objectFit}
      onImageClicked={onImageClicked}
      style={style}
      isOwner={isOwner}
    />
  );
};

export default GameImageRenderer;

export const ImgStyled = styled.img<{
  $objectFit?: string;
  $clickable?: boolean;
  $brightness?: number | null;
}>`
  width: 100%;
  height: 100%;
  filter: ${({ $brightness }) =>
    /** 0.5 is no change in brightness, but needs to be translated into the value of 1 for CSS */

    $brightness && `brightness(${($brightness || 0.5) + 0.5})`};

  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
    `}
  ${({ $objectFit = 'contain' }) =>
    $objectFit &&
    css`
      object-fit: ${$objectFit};
    `}
`;
const SvgImg = styled.div`
  width: 100%;
  height: 100%;
  svg {
    width: 100%;
    height: auto;
  }
`;
